import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import { useOrganizationId } from './useOrganizationId';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useUserProfile } from '@/contexts/UserProfileContext';

type Venue = Tables<'venues'>;
type VenueInsert = TablesInsert<'venues'>;
type VenueUpdate = TablesUpdate<'venues'>;

// Cache times for venue queries (static data)
const VENUE_CACHE_TIME = 30 * 60 * 1000; // 30 minutes - venues rarely change

export function useVenues() {
  const { session } = useUserProfile();
  const organizationId = useOrganizationId();
  
  return useQuery({
    queryKey: ['venues', organizationId],
    staleTime: VENUE_CACHE_TIME,
    gcTime: VENUE_CACHE_TIME * 2,
    refetchOnMount: false,
    queryFn: async () => {
      
      // Fetch venues without contacts JOIN to avoid RLS issues
      const { data: venues, error } = await supabase
        .from('venues')
        .select(`
          *,
          venue_type:venue_types(id, name)
        `)
        .order('name', { ascending: true });


      if (error) throw error;
      if (!venues) return [];

      // Collect all unique contact IDs (managers and venue contacts)
      const contactIds = new Set<string>();
      venues.forEach(venue => {
        if (venue.managed_by_contact_id) contactIds.add(venue.managed_by_contact_id);
        if (venue.venue_contact_id) contactIds.add(venue.venue_contact_id);
      });

      // Fetch contact names through the same organization-scoped source used by
      // contact dropdowns. A direct contacts query can be partially filtered by
      // RLS for some roles, which previously left valid assignments as "Unknown".
      let contactsMap = new Map<string, { 
        id: string; 
        name: string;
        next_follow_up_date?: string | null;
        follow_up_frequency?: string | null;
      }>();
      if (contactIds.size > 0 && organizationId) {
        const { data: organizationContacts, error: organizationContactsError } = await supabase
          .rpc('get_venue_contacts_for_org', { _org_id: organizationId });

        if (!organizationContactsError && organizationContacts) {
          organizationContacts.forEach(contact => {
            if (contactIds.has(contact.id)) {
              contactsMap.set(contact.id, {
                id: contact.id,
                name: contact.name,
              });
            }
          });
        }

        // Preserve follow-up details wherever the current role can read them.
        const { data: contacts, error: contactsError } = await supabase
          .from('contacts')
          .select('id, name, next_follow_up_date, follow_up_frequency')
          .in('id', Array.from(contactIds));

        if (!contactsError && contacts) {
          contacts.forEach(contact => {
            contactsMap.set(contact.id, {
              ...contactsMap.get(contact.id),
              ...contact,
            });
          });
        }
      }

      // Enrich venues with manager and venue contact names
      return venues.map(venue => ({
        ...venue,
        manager: venue.managed_by_contact_id
          ? contactsMap.get(venue.managed_by_contact_id) || { id: venue.managed_by_contact_id, name: 'Unknown' }
          : null,
        venueContact: venue.venue_contact_id
          ? contactsMap.get(venue.venue_contact_id) || { id: venue.venue_contact_id, name: 'Unknown' }
          : null,
      }));
    },
    enabled: !!session && !!organizationId,
  });
}

export function useVenueTypes() {
  return useQuery({
    queryKey: ['venue-types'],
    staleTime: 60 * 60 * 1000, // 1 hour - venue types almost never change
    gcTime: 2 * 60 * 60 * 1000,
    refetchOnMount: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('venue_types')
        .select('*')
        .order('name');

      if (error) throw error;
      return data;
    },
  });
}

export function useVenueManagers() {
  return useQuery({
    queryKey: ['venue-managers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contacts')
        .select('id, name, role')
        .in('role', ['venue_manager', 'booking_manager', 'organizer'])
        .order('name');

      if (error) throw error;
      return data?.map(manager => ({
        id: manager.id,
        name: manager.name,
        role: manager.role
      })) || [];
    },
  });
}

export function useVenueContacts() {
  const { organizationId } = useOrganization();
  
  return useQuery({
    queryKey: ['venue-contacts', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      
      // Use security definer function to bypass RLS
      const { data, error } = await supabase
        .rpc('get_venue_contacts_for_org', { _org_id: organizationId });

      if (error) throw error;
      return data?.map((contact: { id: string; name: string; role: string }) => ({
        id: contact.id,
        name: contact.name,
        role: contact.role
      })) || [];
    },
    enabled: !!organizationId,
  });
}

export function useCreateVenue() {
  const queryClient = useQueryClient();
  const organizationId = useOrganizationId();

  return useMutation({
    mutationFn: async (venue: VenueInsert) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('venues')
        .insert({
          ...venue,
          created_by_user_id: user.id,
          organization_id: organizationId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venues'] });
      queryClient.invalidateQueries({ queryKey: ['venues-select'] });
    },
  });
}

export function useUpdateVenue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...venue }: VenueUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('venues')
        .update(venue)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venues'] });
      queryClient.invalidateQueries({ queryKey: ['venues-select'] });
    },
  });
}

export function useDeleteVenue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('venues')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venues'] });
      queryClient.invalidateQueries({ queryKey: ['venues-select'] });
    },
  });
}
